// عميل الـ API للخادم على Cloudflare Workers.
//
// مبدأ أساسي هنا: لا نزيّف نجاحاً أبداً. إن تعذّر الوصول للخادم نرمي خطأً
// واضحاً ليراه المستخدم، بدل إظهار رسالة "تم الإرسال بنجاح" ولا شيء أُرسل.

const RAW_BASE = import.meta.env.VITE_API_BASE ?? '';
export const API_BASE = RAW_BASE.replace(/\/$/, '');

/** هل جرى ضبط خادم لهذا البناء؟ */
export const isApiConfigured = () => API_BASE.length > 0;

class ApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

const NETWORK_MESSAGE =
  'تعذر الاتصال بخادم التطبيق. تحقق من اتصالك بالإنترنت وحاول مجدداً.';

const NOT_CONFIGURED_MESSAGE =
  'هذه الميزة تحتاج اتصالاً بالخادم، ولم يُضبط بعد في هذه النسخة.';

const TOKEN_KEY = 'moslam_auth_token';

export const getAuthToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
};

/** يُطلق حين يتغيّر صاحب الجلسة: دخولاً أو خروجاً. */
export const AUTH_CHANGED_EVENT = 'moslam:auth-changed';

const announceAuthChange = (before) => {
  if (typeof window !== 'undefined' && getAuthToken() !== before) {
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
  }
};

export const setAuthToken = (token) => {
  const before = getAuthToken();
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
  announceAuthChange(before);
};

export const clearAuthToken = () => {
  const before = getAuthToken();
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {}
  announceAuthChange(before);
};

async function request(path, { method = 'GET', body, headers, signal } = {}) {
  if (!isApiConfigured()) {
    throw new ApiError(NOT_CONFIGURED_MESSAGE, 0, 'NOT_CONFIGURED');
  }

  const isFormData = body instanceof FormData;
  const token = getAuthToken();

  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      // الجلسة في كوكي HttpOnly وتوكن في ترويسة Authorization
      credentials: 'include',
      headers: {
        ...(isFormData ? {} : body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new ApiError(NETWORK_MESSAGE, 0, 'NETWORK');
  }

  let payload = null;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    payload = await response.json().catch(() => null);
  }

  if (!response.ok) {
    throw new ApiError(
      payload?.error || `فشل الطلب (${response.status})`,
      response.status,
      payload?.code
    );
  }

  return payload;
}

/* ------------------------------- المصادقة ------------------------------- */

/*
  التسجيل خطوتان: البريد وكلمة المرور، ثم الرمز الذي وصل البريد. والحساب لا
  يُنشأ إلا في الثانية — قبلها لا حساب، فلا يُسجَّل ببريد لا يملكه صاحبه.
*/
export async function startRegistration(email, password) {
  return request('/api/auth/register/start', { method: 'POST', body: { email, password } });
}

/** كلمة المرور مع الرمز: الخادم يتحقق أنها التي بدأ بها التسجيل، لا كلمة غيرها. */
export async function verifyRegistration(email, code, password) {
  const data = await request('/api/auth/register/verify', { method: 'POST', body: { email, code, password } });
  if (data?.token) setAuthToken(data.token);
  return data.user;
}

/** يرسل رمز الاستعادة. الجواب واحد سواء كان البريد مسجّلاً أم لا. */
export async function requestPasswordReset(email) {
  return request('/api/auth/password/forgot', { method: 'POST', body: { email } });
}

export async function resetPassword(email, code, password) {
  const data = await request('/api/auth/password/reset', { method: 'POST', body: { email, code, password } });
  if (data?.token) setAuthToken(data.token);
  return data.user;
}

export async function login(email, password) {
  const data = await request('/api/auth/login', {
    method: 'POST',
    body: { email, password },
  });
  if (data?.token) setAuthToken(data.token);
  return data.user;
}

export async function logout() {
  clearAuthToken();
  if (!isApiConfigured()) return;
  await request('/api/auth/logout', { method: 'POST' }).catch(() => {});
}

/** يعيد المستخدم الحالي أو null — لا يرمي خطأً عند عدم وجود جلسة. */
export async function fetchCurrentUser() {
  if (!isApiConfigured()) return null;
  try {
    const data = await request('/api/auth/me');
    if (data?.token) setAuthToken(data.token);
    return data?.user ?? null;
  } catch {
    return null;
  }
}

/* ------------------------- الفتاوى والمشاركات ------------------------- */

/** يرفع فتوى صوتية فعلياً. يرمي خطأً إن فشل الرفع. */
export async function submitVoiceQuestion({ audioBlob, askerName, topic, category }) {
  const form = new FormData();
  form.append('audio', audioBlob, 'question.webm');
  form.append('askerName', askerName || '');
  form.append('topic', topic || '');
  form.append('category', category || '');

  return request('/api/questions/voice', { method: 'POST', body: form });
}

export async function submitTextQuestion({ questionText, askerName, category }) {
  return request('/api/questions/text', {
    method: 'POST',
    body: { questionText, askerName, category },
  });
}

export async function submitStudioParticipation(payload) {
  return request('/api/studio/participate', { method: 'POST', body: payload });
}

/* -------------------------------- المجلس -------------------------------- */

/** رابط WebSocket لغرفة المجلس. */
export function majlisSocketUrl(roomId) {
  if (!isApiConfigured()) return null;
  const wsBase = API_BASE.replace(/^http/, 'ws');
  const token = getAuthToken();
  const query = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${wsBase}/api/majlis/${encodeURIComponent(roomId)}/ws${query}`;
}

/* -------------------------- الإعدادات المشتركة -------------------------- */

/** إعدادات يضبطها المسؤول فتصل كل جهاز — خلفية المجلس وشفافيتها. */
export async function fetchAppSettings() {
  if (!isApiConfigured()) return null;
  const data = await request('/api/settings');
  return data?.settings ?? null;
}

export async function updateAppSettings(changes) {
  const data = await request('/api/settings', { method: 'PATCH', body: changes });
  return data?.settings ?? null;
}

/* -------------------------- الإشعارات المجدولة -------------------------- */

export async function fetchScheduledPushes() {
  const data = await request('/api/push/scheduled');
  return data?.scheduled ?? [];
}

/** @param {number} sendAt ثوانٍ منذ ١٩٧٠ — الموعد يُحوّل من توقيت الجهاز */
export async function schedulePush({ title, message, sendAt, sound = 'default' }) {
  const data = await request('/api/push/scheduled', {
    method: 'POST',
    body: { title, message, sendAt, sound },
  });
  return data?.scheduled ?? null;
}

export async function cancelScheduledPush(id) {
  await request(`/api/push/scheduled/${encodeURIComponent(id)}`, { method: 'DELETE' });
  return true;
}

/* -------------------------------- الدروس -------------------------------- */

/** الدروس المنشورة. المستمع يرى المفعّل منها، والمسؤول يراها كلها. */
export async function fetchLessons() {
  if (!isApiConfigured()) return [];
  const data = await request('/api/lessons');
  return data?.lessons ?? [];
}

export async function createLesson(lesson) {
  const data = await request('/api/lessons', { method: 'POST', body: lesson });
  return data?.lesson ?? null;
}

export async function updateLesson(id, changes) {
  const data = await request(`/api/lessons/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: changes,
  });
  return data?.lesson ?? null;
}

export async function deleteLesson(id) {
  await request(`/api/lessons/${encodeURIComponent(id)}`, { method: 'DELETE' });
  return true;
}

/** خط الاستديو: طابور الاتصال الصوتي بين المستمع والمسؤول. */
export function studioLineSocketUrl() {
  if (!isApiConfigured()) return null;
  const wsBase = API_BASE.replace(/^http/, 'ws');
  const token = getAuthToken();
  const query = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${wsBase}/api/studio/line/ws${query}`;
}

/**
 * يرفع صورة غلاف ويعيد رابطها على نطاقنا.
 *
 * الرابط الخارجي يبقى متاحاً، لكن المرفوع لا يختفي ولا يتغيّر تحت يدنا.
 */
export async function uploadCoverImage(file) {
  if (!isApiConfigured()) {
    throw new ApiError('رفع الصور يحتاج خادماً، ولم يُضبط في هذا البناء.');
  }

  const form = new FormData();
  form.append('image', file);

  const token = getAuthToken();
  const res = await fetch(`${API_BASE}/api/uploads/image`, {
    method: 'POST',
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });

  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(data?.error ?? 'تعذّر رفع الصورة.', res.status);
  return data.url;
}

/** حالة غرفة المجلس دون فتح وصلة — لمعرفة أهي مفتوحة قبل الاتصال. */
export async function fetchMajlisState(roomId) {
  if (!isApiConfigured()) return null;
  try {
    const res = await fetch(`${API_BASE}/api/majlis/${encodeURIComponent(roomId)}/state`, {
      credentials: 'include',
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/* -------------------- الإذاعات والمعرض (محتوى مشترك) -------------------- */

/** القائمة المنشورة. فارغة تعني: لم يُنشر شيء، فتُستعمل المدمجة. */
export async function fetchStations() {
  const data = await request('/api/stations');
  return data?.stations ?? [];
}

/** ينشر القائمة كاملة فتصل كل جهاز. للمسؤول. */
export async function publishStations(stations) {
  const data = await request('/api/stations', { method: 'PUT', body: { stations } });
  return data?.count ?? 0;
}

export async function fetchGallery() {
  const data = await request('/api/gallery');
  return data?.images ?? [];
}

export async function addGalleryImage({ title, url }) {
  const data = await request('/api/gallery', { method: 'POST', body: { title, url } });
  return data?.image ?? null;
}

export async function deleteGalleryImage(id) {
  await request(`/api/gallery/${encodeURIComponent(id)}`, { method: 'DELETE' });
  return true;
}

/* ------------------------------ المذيعون ------------------------------ */

/** مفاتيح صلاحيات البث — المصدر في worker/src/lib.ts. */
export const PERMISSIONS = {
  ANSWER_CALLS: 'answer_calls',
  OPEN_ROOM: 'open_room',
  MANAGE_ROOM: 'manage_room',
};

export const PERMISSION_LABELS = {
  [PERMISSIONS.ANSWER_CALLS]: 'الرد على المكالمات',
  [PERMISSIONS.OPEN_ROOM]: 'فتح المجلس وإغلاقه',
  [PERMISSIONS.MANAGE_ROOM]: 'إدارة المجلس',
};

export const PERMISSION_NOTES = {
  [PERMISSIONS.ANSWER_CALLS]: 'يرد على المتصلين بالاستديو ويعتذر وينهي المكالمة.',
  [PERMISSIONS.OPEN_ROOM]: 'يفتح غرفة المجلس فيدخلها الناس، ويغلقها فيخرجون.',
  [PERMISSIONS.MANAGE_ROOM]: 'يقبل طلبات الميكروفون ويقفلها، وينزل من المنصّة ويحظر.',
};

/** الأسئلة الواردة — يقرؤها المسؤول والمذيع. */
export async function fetchIncomingQuestions() {
  const data = await request('/api/admin/questions');
  return data?.questions ?? [];
}

/* -------------------------------- الأذان -------------------------------- */

export async function fetchAdhans() {
  const data = await request('/api/adhans');
  return data?.adhans ?? [];
}

export async function uploadAdhan(file, title) {
  if (!isApiConfigured()) {
    throw new ApiError('رفع الأذان يحتاج خادماً، ولم يُضبط في هذا البناء.');
  }
  const form = new FormData();
  form.append('audio', file);
  form.append('title', title);

  const res = await fetch(`${API_BASE}/api/adhans`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(data?.error ?? 'تعذّر رفع الأذان.', res.status);
  return data.adhan;
}

export async function deleteAdhan(id) {
  await request(`/api/adhans/${encodeURIComponent(id)}`, { method: 'DELETE' });
  return true;
}

/** رابط تسجيل السؤال — محميّ، يُفتح بجلسة المسؤول أو المذيع. */
export function questionAudioUrl(id) {
  if (!isApiConfigured()) return null;
  return `${API_BASE}/api/admin/questions/${encodeURIComponent(id)}/audio`;
}

/** يردّ على سؤال أو يغيّر حالته. */
export async function answerQuestion(id, { answer, status }) {
  const data = await request(`/api/admin/questions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: { answer, status },
  });
  return data?.question ?? null;
}

/** القائمة ومعها: هل صاحب الجلسة المالك فيملك تعيين المسؤولين؟ */
export async function fetchStaffInfo() {
  const data = await request('/api/admin/staff');
  return { staff: data?.staff ?? [], canManageAdmins: Boolean(data?.canManageAdmins) };
}

/** @param {'admin'|'listener'} role — للمالك وحده */
export async function setStaffRole(email, role) {
  const data = await request('/api/admin/staff/role', { method: 'POST', body: { email, role } });
  return data?.staff ?? null;
}

export async function setStaffPermissions(email, permissions) {
  const data = await request('/api/admin/staff', {
    method: 'POST',
    body: { email, permissions },
  });
  return data?.staff ?? null;
}

export { ApiError };

/* -------------------------------- البلاغات -------------------------------- */

/** يُبلغ عن رسالة أو شخص. `context`: majlis | call | other */
export async function submitReport({ context, reason, targetUserId, targetName, content, details }) {
  return request('/api/reports', {
    method: 'POST',
    body: { context, reason, targetUserId, targetName, content, details },
  });
}

export async function fetchReports() {
  const data = await request('/api/admin/reports');
  return data?.reports ?? [];
}

export async function updateReportStatus(id, status) {
  return request(`/api/admin/reports/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: { status },
  });
}

/* ------------------------------ مجتمع الدعاء ------------------------------ */

/** طلبات الدعاء، الأحدث أولاً. before: وقت آخر طلب ظاهر لجلب ما قبله. */
export async function fetchDuas(before) {
  const data = await request(`/api/duas${before ? `?before=${encodeURIComponent(before)}` : ''}`);
  return data?.duas ?? [];
}

export const postDua = (text, anonymous) => request('/api/duas', { method: 'POST', body: { text, anonymous } });

export async function prayForDua(id) {
  const data = await request(`/api/duas/${encodeURIComponent(id)}/pray`, { method: 'POST' });
  return data?.prayers ?? 0;
}

export const flagDua = (id) => request(`/api/duas/${encodeURIComponent(id)}/flag`, { method: 'POST' });

export const deleteDua = (id) => request(`/api/duas/${encodeURIComponent(id)}`, { method: 'DELETE' });

/* ------------------------------ مكتبة الأصوات ------------------------------ */

/** أصوات نوعٍ (adhan | dua | noorania | notify)، أو كلها. مفتوحة للجميع. */
export async function fetchAudio(kind) {
  if (!isApiConfigured()) return [];
  const data = await request(`/api/audio${kind ? `?kind=${encodeURIComponent(kind)}` : ''}`);
  return data?.items ?? [];
}

/** يرفع ملف صوت (للمسؤول). ref: ما يُسمع فيه الصوت، كحرف النورانية. */
export async function uploadAudio({ kind, ref = '', title = '', credit = '', file }) {
  const form = new FormData();
  form.set('kind', kind);
  form.set('ref', ref);
  form.set('title', title);
  form.set('credit', credit);
  form.set('audio', file);
  const data = await request('/api/audio', { method: 'POST', body: form });
  return data?.item ?? null;
}

/** يستورد صوتاً من ويكيميديا كومنز (للمسؤول). */
export async function importAudio({ kind, title, credit, url }) {
  const data = await request('/api/audio/import', { method: 'POST', body: { kind, title, credit, url } });
  return data?.item ?? null;
}

export const deleteAudio = (id) => request(`/api/audio/${encodeURIComponent(id)}`, { method: 'DELETE' });
